import { supabase } from '@/integrations/supabase/client';
import { devLog } from '@/utils/logger';
import { computeDerivedTotals, resolveDisplayTotals } from './totals';
import type { 
  Payslip, 
  PayslipsResponse, 
  PayslipWithLineItems, 
  PayslipDetailsResponse, 
  PayslipLineItem,
  LineItemBreakdown 
} from './types';

export const payslipService = {
  /**
   * Get all payslips for a specific profile
   */
  getPayslips: async (profileId: string): Promise<PayslipsResponse> => {
    const { data, error } = await supabase
      .from('payslips')
      .select('*')
      .eq('profile_id', profileId)
      .order('start_date', { ascending: false });

    if (error) {
      console.error('[payslipService] Error fetching payslips:', error);
      throw new Error(`Failed to fetch payslips: ${error.message}`);
    }

    const payslips = data || [];
    
    // Calculate summary data
    const summary = {
      totalEarnings: payslips.reduce((total, payslip) => total + Number(payslip.gross_pay), 0),
      totalHours: payslips.reduce((total, payslip) => total + Number(payslip.total_hours), 0),
      payslipCount: payslips.length
    };

    return { payslips, summary };
  },

  /**
   * Get detailed payslip with line items
   */
  getPayslipDetails: async (payslipId: string): Promise<PayslipDetailsResponse> => {
    devLog('[payslipService] Fetching payslip details for:', payslipId);
    
    // Fetch payslip with line items
    const { data: payslipData, error: payslipError } = await supabase
      .from('payslips')
      .select(`
        *,
        line_items:payslip_line_items(*)
      `)
      .eq('id', payslipId)
      .single();

    if (payslipError) {
      console.error('[payslipService] Error fetching payslip details:', payslipError);
      throw new Error(`Failed to fetch payslip details: ${payslipError.message}`);
    }

    // Get previous payslip for comparison
    const { data: previousPayslip } = await supabase
      .from('payslips')
      .select('*')
      .eq('profile_id', payslipData.profile_id)
      .lt('start_date', payslipData.start_date)
      .order('start_date', { ascending: false })
      .limit(1)
      .maybeSingle();

    // Calculate YTD summary — anchored on the payslip's own period year (string
    // math, no timezone shift) and fail-loud so errors never masquerade as zeroes.
    const periodYear = String(payslipData.start_date).slice(0, 4);
    const startOfYear = `${periodYear}-01-01`;
    const { data: ytdPayslips, error: ytdError } = await supabase
      .from('payslips')
      .select('gross_pay, net_pay, total_hours')
      .eq('profile_id', payslipData.profile_id)
      .gte('start_date', startOfYear)
      .lte('start_date', payslipData.start_date);

    if (ytdError) {
      console.error('[payslipService] Error fetching YTD payslips:', ytdError);
      throw new Error(`Failed to fetch year-to-date summary: ${ytdError.message}`);
    }

    const yearToDateSummary = {
      total_gross: ytdPayslips?.reduce((sum, p) => sum + Number(p.gross_pay || 0), 0) || 0,
      total_net: ytdPayslips?.reduce((sum, p) => sum + Number(p.net_pay || 0), 0) || 0,
      total_hours: ytdPayslips?.reduce((sum, p) => sum + Number(p.total_hours || 0), 0) || 0,
      periods_count: ytdPayslips?.length || 0
    };

    // Calculate enhanced totals from line items
    const lineItems = payslipData.line_items || [];
    const derived = computeDerivedTotals(lineItems as PayslipLineItem[]);
    const display = resolveDisplayTotals(payslipData, derived);
    const calculated_totals = {
      gross_earnings: display.gross_earnings,
      total_deductions: display.total_deductions,
      net_pay: display.net_pay,
      tax_breakdown: display.tax_breakdown,
      ytd_totals: {
        gross_earnings: yearToDateSummary.total_gross,
        net_pay: yearToDateSummary.total_net,
        total_deductions: 0,
      },
    };

    const payslipWithLineItems: PayslipWithLineItems = {
      ...payslipData,
      line_items: lineItems as PayslipLineItem[],
      calculated_totals
    };

    return {
      payslip: payslipWithLineItems,
      previous_payslip: previousPayslip || undefined,
      year_to_date_summary: yearToDateSummary
    };
  },

  /**
   * Get line items for a specific payslip
   */
  getPayslipLineItems: async (payslipId: string): Promise<PayslipLineItem[]> => {
    devLog('[payslipService] Fetching line items for payslip:', payslipId);
    
    const { data, error } = await supabase
      .from('payslip_line_items')
      .select('*')
      .eq('payslip_id', payslipId)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('[payslipService] Error fetching line items:', error);
      throw new Error(`Failed to fetch line items: ${error.message}`);
    }

    return data || [];
  },

  /**
   * Get line items breakdown by category
   */
  getLineItemBreakdown: async (payslipId: string): Promise<LineItemBreakdown> => {
    const lineItems = await payslipService.getPayslipLineItems(payslipId);
    
    return {
      earnings: lineItems.filter(item => item.category === 'earnings'),
      deductions: lineItems.filter(item => item.category === 'deductions'),
      taxes: lineItems.filter(item => item.category === 'taxes'),
      benefits: lineItems.filter(item => item.category === 'benefits')
    };
  },

  /**
   * Calculate enhanced payslip totals from line items
   */
  calculatePayslipTotals: (lineItems: PayslipLineItem[]) => {
    const derived = computeDerivedTotals(lineItems);
    return {
      gross_earnings: derived.gross_earnings,
      total_deductions: derived.total_deductions,
      net_pay: derived.net_pay,
      tax_breakdown: derived.tax_breakdown,
      ytd_totals: {
        gross_earnings: 0, // Filled in by getPayslipDetails from the YTD query
        net_pay: 0,
        total_deductions: 0
      }
    };
  }
};
